// Fast transits (a day to a few days). cls: "conjunction" | "hard" (square, opposition) | "soft" (trine, sextile).
// text: 1 to 2 sentences, 20-40 words. prompt: one reflection question, under 16 words.
// Natal keys: sun, moon, mercury, venus, mars, jupiter, saturn, asc, mc.
// Notes: transiting Sun conjunct natal Sun is the solar return (birthday); Moon conjunct Moon is the
// monthly lunar return; Mercury, Venus and Mars conjunct themselves are their yearly or two-yearly returns.
// Moon transits last hours; Sun, Mercury and Venus a few days; Mars a few days to a week.

export const TRANSIT_FAST = {
  moon: {
    sun: {
      conjunction: {
        text: "The Moon passes over your Sun today, so how you feel and what you want point the same way for once. Good day to start something small that is plainly yours.",
        prompt: "What would you do today if nobody needed anything from you?",
      },
      hard: {
        text: "Your mood and your aims pull in different directions today. Expect some friction between what you need and what you planned, and give the need a few minutes before overriding it.",
        prompt: "Which feeling today kept interrupting your plans, and what did it want?",
      },
      soft: {
        text: "Feelings and goals cooperate today, so ordinary tasks go down easier than usual. Use the easy hours for something you have been putting off because it felt heavy.",
        prompt: "What felt easy today that usually takes effort?",
      },
    },
    moon: {
      conjunction: {
        text: "Your lunar return: the Moon is back where it was when you were born. A good day to notice what comforts you now and whether it still works.",
        prompt: "What actually made you feel at home this month?",
      },
      hard: {
        text: "Old moods may surface today without a clear reason. Treat them as weather, name them out loud if it helps, and hold off on decisions that only make sense while upset.",
        prompt: "Is the feeling you have today new, or a familiar visitor?",
      },
      soft: {
        text: "Your instincts are easy to read today, and you settle into routines without fuss. Cook something, or call someone who knows you well.",
        prompt: "Which small routine keeps you steady, and did you do it today?",
      },
    },
    mercury: {
      conjunction: {
        text: "Thoughts and feelings arrive together today, so you can say what you mean with less editing. Write things down; notes made today tend to be more honest than usual.",
        prompt: "What have you been thinking but not saying?",
      },
      hard: {
        text: "Your head and your gut disagree today. You may say something sharper than you meant or chew over a small remark for hours, so reread messages before sending.",
        prompt: "Which conversation today stuck with you longer than it should have?",
      },
      soft: {
        text: "Talking things through helps today. Feelings that felt tangled can be put into plain words, which makes this a good day for a frank and kind conversation.",
        prompt: "What became clearer once you said it out loud?",
      },
    },
    venus: {
      conjunction: {
        text: "You want comfort and beauty today, and you are easier to be around. Give some time to something pleasant for its own sake: a good meal, music, a slow walk.",
        prompt: "What small pleasure did you allow yourself today?",
      },
      hard: {
        text: "You may want more ease than the day can give, and small indulgences look tempting. Enjoy what you enjoy, and notice whether you are soothing a feeling or dodging it.",
        prompt: "What were you reaching for when you reached for comfort today?",
      },
      soft: {
        text: "Warmth comes easily today, and people respond to it. A good day to thank someone or mend a small rift.",
        prompt: "Who made your day better, and did you tell them?",
      },
    },
    mars: {
      conjunction: {
        text: "Your feelings run hot today and want action. Put the heat into something physical or a task with a clear finish line, and give your temper a longer fuse.",
        prompt: "What made you want to act fast today?",
      },
      hard: {
        text: "Irritation sits close to the surface today. Small things may set you off, so move your body and save the important arguments for a calmer day.",
        prompt: "What was the irritation today really about?",
      },
      soft: {
        text: "You have a useful push today, enough to start and finish things without strain. Good for a workout, or for saying a clear no to something.",
        prompt: "What did you get done today just because you started?",
      },
    },
    jupiter: {
      conjunction: {
        text: "You feel generous and hopeful today, maybe a little larger than life. Enjoy the lift, and keep an eye on promises and plans that grow past what is practical.",
        prompt: "Where could a little more hope help you right now?",
      },
      hard: {
        text: "Today tilts toward too much: too many plans, too big a plate, too large a reaction. Trim one thing back and the rest of the day will feel lighter.",
        prompt: "Where did you overdo it today, and what were you hoping to feel?",
      },
      soft: {
        text: "A kind, easy mood today. Good for being generous, or for giving someone the benefit of the doubt you would want for yourself.",
        prompt: "What are you grateful for that you rarely mention?",
      },
    },
    saturn: {
      conjunction: {
        text: "You may feel heavier or more serious today, and that is fine. Use the sober mood to sort out one responsibility instead of fighting the feeling all day.",
        prompt: "Which responsibility would feel lighter if you dealt with it today?",
      },
      hard: {
        text: "Today can feel like a lot of effort for small returns, and you may be harder on yourself than usual. Lower the bar slightly and finish one thing well.",
        prompt: "What would you say to a friend who felt the way you do today?",
      },
      soft: {
        text: "Steady, quiet focus is available today. Good for planning your week, or for keeping a commitment you made to yourself and nobody else.",
        prompt: "What commitment to yourself did you keep this week?",
      },
    },
    asc: {
      conjunction: {
        text: "Your feelings show on your face today, whether you like it or not. Let it show: be open about how you are, and take care of your body's basic needs.",
        prompt: "How did your body feel today, before you thought about it?",
      },
      hard: {
        text: "Your mood and the way you come across may not match today. If people read you wrong, a quick plain explanation saves a lot of second guessing later.",
        prompt: "Where did you seem one way today while feeling another?",
      },
      soft: {
        text: "You come across easily today, and first impressions go well. A good day to meet someone new or ask a small favor.",
        prompt: "Who did you meet or notice today, and why did they stand out?",
      },
    },
    mc: {
      conjunction: {
        text: "Your mood is on display in public today, at work or in your community. Let people see that you care about what you do, and save the venting for a friend.",
        prompt: "What do you want to be known for, in one sentence?",
      },
      hard: {
        text: "Home and outside demands tug at each other today. You may feel pulled between duties, so decide in advance which one gets your best hour.",
        prompt: "Which demand got your best hour today, and was it the right one?",
      },
      soft: {
        text: "Your instincts serve your work today. A good day for a quiet step forward on a goal, or a conversation with someone whose respect matters to you.",
        prompt: "What small step toward a larger aim could you take tomorrow?",
      },
    },
  },

  sun: {
    sun: {
      conjunction: {
        text: "Your solar return: the Sun is back where it was when you were born. A natural time to look back on the year and choose one intention for the next.",
        prompt: "What do you want this next year of your life to be about?",
      },
      hard: {
        text: "A checkpoint in your year, with the Sun at a sharp angle to where it sat at your birth. Notice where your effort is landing and where you have drifted from your aims.",
        prompt: "What have you drifted from since your last birthday?",
      },
      soft: {
        text: "The Sun backs up your own Sun for a few days, and things that rely on you being yourself go smoothly. Good for asking for something, or for a personal project.",
        prompt: "Where does being plainly yourself make things easier?",
      },
    },
    moon: {
      conjunction: {
        text: "The Sun lights up your Moon for a few days, bringing private needs into view. Pay attention to what you need to feel safe, and say it to someone.",
        prompt: "What do you need more of at home right now?",
      },
      hard: {
        text: "What you want out in the world and what you need in private are at odds for a few days. Make room for rest even when ambition is loud.",
        prompt: "What are you neglecting at home to keep up elsewhere?",
      },
      soft: {
        text: "Inner and outer life cooperate for a few days. A good stretch to look after your home and whatever gives you a sense of belonging.",
        prompt: "Where do you feel most like you belong lately?",
      },
    },
    mercury: {
      conjunction: {
        text: "The Sun crosses your Mercury, making your thinking clearer and more confident for a few days. Good for presenting an idea, or for a conversation you have been avoiding.",
        prompt: "What idea of yours deserves a wider audience?",
      },
      hard: {
        text: "For a few days you may feel strongly attached to your own opinions. Make your case, and leave room for someone else to be right about the details.",
        prompt: "Where did you argue for the sake of being right?",
      },
      soft: {
        text: "Ideas come easily and land well for a few days. Use the stretch for the emails you have put off, or for explaining something tricky.",
        prompt: "What did you learn this week that you want to remember?",
      },
    },
    venus: {
      conjunction: {
        text: "The Sun highlights your Venus, so what you enjoy and value comes forward. A good few days for friendship and for treating yourself kindly.",
        prompt: "What do you love doing that you have not done lately?",
      },
      hard: {
        text: "For a few days you may notice a gap between what you want and what you have. Look at it plainly; the gap tells you what you value right now.",
        prompt: "What are you wanting lately, and what does that say about you?",
      },
      soft: {
        text: "A pleasant few days for friendships and for enjoying where you are. People find you easy to like, so be generous with your company.",
        prompt: "What made you feel at ease with someone this week?",
      },
    },
    mars: {
      conjunction: {
        text: "The Sun fires up your Mars, giving you drive and a shorter fuse for a few days. Point the push at a real goal and watch for impatience with others.",
        prompt: "What goal could use this push before it fades?",
      },
      hard: {
        text: "Friction is likely for a few days, with others or with your own limits. Push where it counts, and skip fights that only drain you.",
        prompt: "Which fight this week was worth having, and which was not?",
      },
      soft: {
        text: "Your drive and your sense of purpose work together for a few days. Good for tackling a hard task, or for standing up for something you care about.",
        prompt: "What did you go after this week without second guessing?",
      },
    },
    jupiter: {
      conjunction: {
        text: "The Sun meets your Jupiter, a hopeful stretch when good will feels closer at hand. Say yes to learning and generosity, and read the fine print on big ideas.",
        prompt: "What would you try if you trusted it might work?",
      },
      hard: {
        text: "Optimism may outrun your schedule for a few days. Keep the ambitions, and count the hours honestly before you commit to every exciting thing.",
        prompt: "What have you said yes to that you cannot actually fit in?",
      },
      soft: {
        text: "A helpful, open-handed few days. Good for study or travel plans, or for reaching out to someone who has been on your mind.",
        prompt: "Who could teach you something you want to learn?",
      },
    },
    saturn: {
      conjunction: {
        text: "The Sun meets your Saturn, a sober few days for checking your commitments. You may feel tired or tested, and steady work done now holds up well later.",
        prompt: "Which commitment are you ready to take more seriously?",
      },
      hard: {
        text: "For a few days, obstacles may feel personal. Mostly they point to where your plans need more structure, so adjust the plan before you blame yourself.",
        prompt: "What is this obstacle telling you about the plan?",
      },
      soft: {
        text: "Discipline comes more naturally for a few days. Good for long-range planning, or for finally taking on a responsibility you have been circling.",
        prompt: "What would future you thank you for starting this week?",
      },
    },
    asc: {
      conjunction: {
        text: "The Sun crosses your Rising point (the Ascendant), a fresh start each year. You are more visible, so refresh your look or how you introduce yourself.",
        prompt: "How do you want to show up this year?",
      },
      hard: {
        text: "For a few days, how you come across may get tested by other people's expectations. Hold your ground kindly, and notice who you shrink around.",
        prompt: "Around whom do you make yourself smaller, and why?",
      },
      soft: {
        text: "You make a good impression for a few days without much effort. Use it to take the first step, or to ask for what you need.",
        prompt: "What would you ask for if you assumed the answer was yes?",
      },
    },
    mc: {
      conjunction: {
        text: "The Sun reaches the top of your chart, the most visible point in your year. A good stretch to show your work and take credit for it.",
        prompt: "What have you done this year that you are proud of?",
      },
      hard: {
        text: "Work and home both want you for a few days. Decide which one cannot wait, and tell the other plainly when it will get its turn.",
        prompt: "Which part of your life wants more of you right now?",
      },
      soft: {
        text: "Your efforts get noticed more easily for a few days. Good for asking for feedback, or for making your aims known to people who can help.",
        prompt: "Who should know what you are working toward?",
      },
    },
  },

  mercury: {
    sun: {
      conjunction: {
        text: "Mercury meets your Sun, and your mind is quick and talkative for a few days. Good for pitching, and for saying who you are in clear words.",
        prompt: "How would you describe yourself today in one sentence?",
      },
      hard: {
        text: "Thinking may feel scattered or argumentative for a day or two. Double check plans and messages, and listen once more before you answer.",
        prompt: "What did you misread or rush today?",
      },
      soft: {
        text: "Clear thinking about yourself comes easily for a day or two. A good time to set priorities, or to explain your plans to someone.",
        prompt: "What sits at the top of your list right now, honestly?",
      },
    },
    moon: {
      conjunction: {
        text: "Mercury touches your Moon, so you think about how you feel and want to talk it through. A good day to write in a journal or have a heart to heart.",
        prompt: "What feeling have you been trying to explain to yourself?",
      },
      hard: {
        text: "Feelings and logic trip over each other for a day or two. You may say too much or too little, so let a conversation wait if it is getting heated.",
        prompt: "What did you say today that you wish you had said differently?",
      },
      soft: {
        text: "Talking about feelings goes well for a day or two. Check in with family or housemates, or write down what has been on your mind.",
        prompt: "Who would like to hear from you this week?",
      },
    },
    mercury: {
      conjunction: {
        text: "Mercury returns to where it was at your birth, roughly once a year. Your natural way of thinking feels sharp, so take on the mental work you do best.",
        prompt: "What kind of problem do you most enjoy solving?",
      },
      hard: {
        text: "Your usual way of thinking meets resistance for a day or two. Misunderstandings come easily, so spell things out and ask questions instead of assuming.",
        prompt: "What did you assume today that turned out wrong?",
      },
      soft: {
        text: "Ideas flow in your natural style for a day or two. Good for study and for any conversation that needs a clear head.",
        prompt: "What are you curious about this week?",
      },
    },
    venus: {
      conjunction: {
        text: "Mercury meets your Venus, making your words kinder and more graceful for a day or two. Good for compliments and thank you notes.",
        prompt: "What kind thing could you say to someone today?",
      },
      hard: {
        text: "For a day or two you may say what is pleasant instead of what is true. Say the honest thing kindly.",
        prompt: "Where are you being nice instead of being clear?",
      },
      soft: {
        text: "Easy, friendly conversations for a day or two. A good stretch for smoothing things over or making plans with friends.",
        prompt: "What do you appreciate in your life right now?",
      },
    },
    mars: {
      conjunction: {
        text: "Mercury meets your Mars, sharpening your tongue and your thinking for a day or two. Great for debate and decisive emails, risky for snap replies.",
        prompt: "What do you want to say with conviction?",
      },
      hard: {
        text: "Words come out sharper than you intend for a day or two. Count to ten before replying, and avoid arguing by text.",
        prompt: "What reply did you hold back today, and was that wise?",
      },
      soft: {
        text: "You think fast and act on it well for a day or two. Good for making decisions and saying no clearly.",
        prompt: "What decision have you been delaying that you could make now?",
      },
    },
    jupiter: {
      conjunction: {
        text: "Mercury meets your Jupiter, opening your mind to big ideas for a day or two. Good for planning trips and talking about what you believe.",
        prompt: "What big question has been on your mind lately?",
      },
      hard: {
        text: "Big talk and big plans may outrun the details for a day or two. Enjoy the ideas, then check the specifics before you commit.",
        prompt: "Which plan sounds great but needs a second look?",
      },
      soft: {
        text: "Learning and teaching go well for a day or two. Good for sharing what you know, or for a long conversation about what matters to you.",
        prompt: "What do you know well enough to teach?",
      },
    },
    saturn: {
      conjunction: {
        text: "Mercury meets your Saturn, giving you serious, careful thinking for a day or two. Good for paperwork and for hard conversations that need structure.",
        prompt: "What needs a clear plan before you can move on it?",
      },
      hard: {
        text: "Thinking may lean gloomy or rigid for a day or two. Notice harsh self-talk, and answer it with facts the way a fair friend would.",
        prompt: "What harsh thought about yourself would not hold up to facts?",
      },
      soft: {
        text: "Focused, practical thinking for a day or two. Good for detail work and for making plans you will actually keep.",
        prompt: "What realistic plan could you make today?",
      },
    },
    asc: {
      conjunction: {
        text: "Mercury crosses your Rising point (the Ascendant), making you chatty and quick on your feet for a day or two. Good for introductions and short trips.",
        prompt: "What conversation today left you thinking?",
      },
      hard: {
        text: "Others may misread your words or tone for a day or two. Keep messages simple, and clear up confusion early.",
        prompt: "Where did you feel misunderstood, and what would help?",
      },
      soft: {
        text: "You speak and come across clearly for a day or two. A good time for interviews, or for saying hello first.",
        prompt: "Who could you start a conversation with this week?",
      },
    },
    mc: {
      conjunction: {
        text: "Mercury reaches the top of your chart, putting work talk and plans in the spotlight for a day or two. Good for meetings, and for sharing ideas where people can see them.",
        prompt: "What idea about your work should you share?",
      },
      hard: {
        text: "Work communication and private life compete for attention for a day or two. Keep a running list so nothing important slips.",
        prompt: "What slipped through the cracks this week?",
      },
      soft: {
        text: "Your ideas about work land well for a day or two. A good time to plan next steps or talk with someone further along than you.",
        prompt: "What advice would help your work right now?",
      },
    },
  },

  venus: {
    sun: {
      conjunction: {
        text: "Venus meets your Sun, making you more charming and more interested in pleasure for a few days. Good for creative work and for being kind to yourself.",
        prompt: "What makes you feel most like yourself and most at ease?",
      },
      hard: {
        text: "For a few days you may want ease more than effort. Enjoy yourself, and notice whether a treat is a reward or an escape.",
        prompt: "What are you avoiding by staying comfortable?",
      },
      soft: {
        text: "A warm, sociable few days. People like being around you and creative work comes easily, so make something or gather a few friends.",
        prompt: "What did you make or enjoy this week?",
      },
    },
    moon: {
      conjunction: {
        text: "Venus meets your Moon, a soft, comforting few days. Good for a cozy evening at home, and for looking after yourself without guilt.",
        prompt: "What helps you feel cared for?",
      },
      hard: {
        text: "You may crave reassurance or comfort food for a few days. Give yourself the care you want, and notice what you are asking others to fill.",
        prompt: "What reassurance are you looking for, and can you give it yourself?",
      },
      soft: {
        text: "Home and friendships feel easy and warm for a few days. Host something small, or spend time with people who relax you.",
        prompt: "Where do you feel most relaxed lately?",
      },
    },
    mercury: {
      conjunction: {
        text: "Venus meets your Mercury, making your words graceful for a few days. Good for thank you notes, and for talking through a disagreement kindly.",
        prompt: "What would you like to say more gracefully?",
      },
      hard: {
        text: "Tact and honesty may compete for a few days. Choose your words with care, and keep the truth in them even when it is awkward.",
        prompt: "Where are you sugarcoating something that needs saying?",
      },
      soft: {
        text: "Pleasant, easy conversations for a few days. Good for gentle feedback, or for reconnecting with someone you have drifted from.",
        prompt: "Who have you drifted from that you miss?",
      },
    },
    venus: {
      conjunction: {
        text: "Venus returns to where it was at your birth, about once a year. A good time to notice what you value now and enjoy your own taste.",
        prompt: "What do you value now that you did not a year ago?",
      },
      hard: {
        text: "For a few days your tastes and values get questioned. Ask yourself whether you still want what you used to want.",
        prompt: "What do you want out of habit more than desire?",
      },
      soft: {
        text: "Your sense of style and comfort feels right for a few days. Good for friendships and for making your surroundings pleasant.",
        prompt: "What small change would make your space nicer?",
      },
    },
    mars: {
      conjunction: {
        text: "Venus meets your Mars, blending charm and drive for a few days. Good for creative projects with momentum and for going after what you enjoy.",
        prompt: "What do you want enough to go after?",
      },
      hard: {
        text: "Wanting and getting may feel out of sync for a few days. Impatience or friction with others comes easily, so say what you want plainly.",
        prompt: "What do you want right now that you have not asked for?",
      },
      soft: {
        text: "A lively, sociable few days with an easy push behind them. Good for physical fun, and for turning an enjoyable idea into something real.",
        prompt: "What fun thing could you actually do this week?",
      },
    },
    jupiter: {
      conjunction: {
        text: "Venus meets your Jupiter, a generous, upbeat few days. Enjoy good company and good food, and keep an eye on the urge to overdo it.",
        prompt: "What are you thankful for this week?",
      },
      hard: {
        text: "Indulgence is tempting for a few days. Enjoy yourself, and set one limit in advance so the fun does not cost you tomorrow.",
        prompt: "Where would one limit make the fun better?",
      },
      soft: {
        text: "A friendly, generous few days. Good for celebrations, art, reaching out to people you like, and saying yes to invitations that sound fun.",
        prompt: "Who could you celebrate this week?",
      },
    },
    saturn: {
      conjunction: {
        text: "Venus meets your Saturn, a sober few days for pleasure and friendship. Good for keeping commitments, and for valuing what lasts over what glitters.",
        prompt: "Which friendships have lasted, and what keeps them going?",
      },
      hard: {
        text: "You may feel unappreciated, or short on fun, for a few days. It passes; use it to see what you would like more of.",
        prompt: "What would make you feel more appreciated?",
      },
      soft: {
        text: "A steady few days for friendships and creative discipline. Good for practice, and for showing up for people who count on you.",
        prompt: "What skill would you like to practice regularly?",
      },
    },
    asc: {
      conjunction: {
        text: "Venus crosses your Rising point (the Ascendant), making you warmer and easier to be around for a few days. Good for a haircut, or for meeting new people.",
        prompt: "What makes you feel good in your own skin?",
      },
      hard: {
        text: "For a few days you may want to please others more than suits you. Be friendly, and keep your own preferences in the room.",
        prompt: "Where did you go along with something you did not want?",
      },
      soft: {
        text: "You come across warm and easy for a few days. Good for first meetings and for smoothing over awkwardness.",
        prompt: "What made someone smile at you this week?",
      },
    },
    mc: {
      conjunction: {
        text: "Venus reaches the top of your chart, a few days when people notice your grace and good will. Good for networking and for creative work in public.",
        prompt: "What do people appreciate about how you work?",
      },
      hard: {
        text: "Pleasure and duty compete for a few days. Make room for both, and do the task first so you can enjoy the rest.",
        prompt: "What duty are you postponing for something more fun?",
      },
      soft: {
        text: "Good will at work comes easily for a few days. A pleasant time to ask for help, or to show work you are proud of.",
        prompt: "Who would you enjoy working with more?",
      },
    },
  },

  mars: {
    sun: {
      conjunction: {
        text: "Mars meets your Sun, giving you a burst of drive and confidence for a few days. Start the hard thing, and watch for impatience and rushing.",
        prompt: "What would you start today if you had no doubts?",
      },
      hard: {
        text: "Your will meets resistance for a few days. Push where it is worth it, and give your body a physical outlet.",
        prompt: "Which push this week came from ego, and which from purpose?",
      },
      soft: {
        text: "You have a strong, confident push for a few days. Good for friendly competition and for moving a stalled project forward.",
        prompt: "What stalled project could use your effort now?",
      },
    },
    moon: {
      conjunction: {
        text: "Mars meets your Moon, stirring up feelings for a few days. You may be quicker to anger or tears, so give emotions a physical outlet.",
        prompt: "What is underneath the strong feelings this week?",
      },
      hard: {
        text: "Home and feelings may run tense for a few days. Watch for snapping at people close to you, and do something active to let off steam.",
        prompt: "Who did you snap at, and what were you really upset about?",
      },
      soft: {
        text: "Emotional courage comes more easily for a few days. Good for home projects, or for acting on a gut feeling.",
        prompt: "What gut feeling have you been ignoring?",
      },
    },
    mercury: {
      conjunction: {
        text: "Mars meets your Mercury, sharpening your mind and your tongue for a few days. Great for debate and quick decisions; slow down before hitting send.",
        prompt: "What do you need to say plainly?",
      },
      hard: {
        text: "Arguments flare easily for a few days. Choose your words, slow down when you notice yourself rushing, and leave heated conversations for a cooler day.",
        prompt: "Which argument is worth having, and which can wait?",
      },
      soft: {
        text: "Your mind is fast and decisive for a few days. Good for negotiating, and for getting through a backlog of messages.",
        prompt: "What problem can you solve with a quick decision?",
      },
    },
    venus: {
      conjunction: {
        text: "Mars meets your Venus, adding heat to what you enjoy for a few days. Good for creative work with real momentum and for going after what you like.",
        prompt: "What do you want more of in your life right now?",
      },
      hard: {
        text: "Wanting and harmony clash for a few days. You may feel restless or irritable with others, so say what you want before it turns into friction.",
        prompt: "What desire are you keeping quiet about?",
      },
      soft: {
        text: "Creative drive and charm work together for a few days. Good for playing sports with friends, and for acting on what you enjoy.",
        prompt: "What creative idea could you start this week?",
      },
    },
    mars: {
      conjunction: {
        text: "Mars returns to where it was at your birth, about every two years. A good time to start a new effort and notice what you want to fight for now.",
        prompt: "What do you want to put your effort into for the next two years?",
      },
      hard: {
        text: "Your usual way of acting meets friction for a few days. Notice where you push too hard or not enough, and adjust before it costs you.",
        prompt: "Where are you pushing too hard, or not hard enough?",
      },
      soft: {
        text: "Your drive works in your natural style for a few days. Good for decisive action and for finishing what you started.",
        prompt: "What did you finish this week?",
      },
    },
    jupiter: {
      conjunction: {
        text: "Mars meets your Jupiter, a bold, enthusiastic few days. Good for big efforts and a sensible risk, as long as you stay honest about your limits.",
        prompt: "What bold move have you been considering?",
      },
      hard: {
        text: "Overconfidence is the hazard for a few days. Go after big things, and check your time and stamina before agreeing to more.",
        prompt: "What have you taken on that is more than you can carry?",
      },
      soft: {
        text: "Effort comes with less strain for a few days. Good for ambitious tasks and for standing up for what you believe.",
        prompt: "What do you believe in enough to act on?",
      },
    },
    saturn: {
      conjunction: {
        text: "Mars meets your Saturn, a few days of effort meeting limits. Slow, disciplined work goes well, and forcing things tends to backfire.",
        prompt: "Where does slow and steady beat fast right now?",
      },
      hard: {
        text: "You may feel blocked or frustrated for a few days. Pace yourself, leave alone what will not budge, and put your effort into what will.",
        prompt: "What are you forcing that might need patience instead?",
      },
      soft: {
        text: "Disciplined effort works well for a few days. Good for hard training, and for any work that rewards plain persistence.",
        prompt: "What would steady effort change for you this month?",
      },
    },
    asc: {
      conjunction: {
        text: "Mars crosses your Rising point (the Ascendant), making you bolder and more direct for a few days. Good for taking initiative and exercise. Watch for coming on too strong.",
        prompt: "Where would being more direct help you?",
      },
      hard: {
        text: "You may clash with others or feel pushed around for a few days. Stand your ground, and decline the bait.",
        prompt: "What bait did you avoid or take this week?",
      },
      soft: {
        text: "You come across confident and capable for a few days. Good for taking the lead, or for speaking up.",
        prompt: "Where could you take the lead this week?",
      },
    },
    mc: {
      conjunction: {
        text: "Mars reaches the top of your chart, a few days of ambition and drive at work. Push for what you want, and mind your temper with people in charge.",
        prompt: "What do you want to accomplish this month?",
      },
      hard: {
        text: "Work and home pull hard against each other for a few days. Friction with bosses or family comes easily, so choose your fights carefully.",
        prompt: "Which tension at work or home needs a calm conversation?",
      },
      soft: {
        text: "Ambition flows smoothly for a few days. Good for taking initiative at work and getting noticed for your effort.",
        prompt: "What initiative could you take at work this week?",
      },
    },
  },
};

// The Moon's sign today, as the day's emotional weather for everyone. 1 sentence, under 25 words.
export const MOON_SIGN_TODAY = {
  aries: "Everyone is a little quicker and blunter today, so it is a good day for bold first steps and a poor one for patience.",
  taurus: "The mood slows down today, good for a long meal and for finishing things at an unhurried pace.",
  gemini: "The day runs chatty and a bit scattered, good for errands and quick catch-ups.",
  cancer: "People feel more sensitive today and want to be home, so it is a good day to cook for someone.",
  leo: "The mood runs warm and loud today, good for play and for giving someone the attention they want.",
  virgo: "A practical mood today, good for fixing the small things that bug you.",
  libra: "People want things to feel fair today, so it is a good day to meet someone halfway.",
  scorpio: "Feelings run deep and private today, good for an honest talk or letting go of something stale.",
  sagittarius: "The mood is restless and upbeat today, good for getting outside and talking about the big picture.",
  capricorn: "A serious mood today, good for work and keeping promises, even if people seem a little cool.",
  aquarius: "The mood is cool and inventive today, good for group efforts and stepping back to see the whole pattern.",
  pisces: "A dreamy mood today, good for rest and music, and less good for sharp deadlines.",
};

// The Moon moving through one of YOUR houses today (personal). 1 sentence, under 25 words.
export const MOON_HOUSE_TODAY = {
  1: "The Moon is in your 1st house, so your feelings show plainly today; look after your body and how you meet the world.",
  2: "The Moon is in your 2nd house, so comfort and security matter more today; notice what helps you feel steady.",
  3: "The Moon is in your 3rd house, making it a busy day for messages and errands close to home.",
  4: "The Moon is in your 4th house, so you want home today; cook, or call family.",
  5: "The Moon is in your 5th house, a day for play and making things; do something only because it is fun.",
  6: "The Moon is in your 6th house, so routines and health want attention; clear the small tasks first.",
  7: "The Moon is in your 7th house, putting other people's needs front and center; good for one on one time with friends or colleagues.",
  8: "The Moon is in your 8th house, so feelings run below the surface today; good for private reflection and honest talks.",
  9: "The Moon is in your 9th house, so you want room to roam; plan a trip, or read something that stretches you.",
  10: "The Moon is in your 10th house, so your mood is more visible at work today; show up with care for what you do.",
  11: "The Moon is in your 11th house, so friends lift you today; reach out to one you miss.",
  12: "The Moon is in your 12th house, a quieter, inward day; take time alone without guilt.",
};

// Today's Moon phase, by the name people know from the sky (core.commonMoonPhase:
// the four principal phases only within about 6 degrees, about half a day).
// 1 sentence, under 25 words, practical.
export const MOON_PHASE_TODAY = {
  new: "New Moon, a quiet starting point: set one small intention and keep it to yourself for now.",
  waxingCrescent: "Waxing Crescent Moon, early momentum: take the first practical step on what you started, even if old habits push back.",
  firstQuarter: "First Quarter Moon, a point of decision: something needs action or adjustment, so make the call and move.",
  waxingGibbous: "Waxing Gibbous Moon, time to refine: fix the details, and get ready for results to show.",
  full: "Full Moon, things come to light: notice what you see clearly now, and let strong feelings settle before you act on them.",
  waningGibbous: "The Moon is past full and shrinking, time to share: pass along something useful you have learned.",
  lastQuarter: "Last Quarter Moon, time to reassess: drop what is no longer working and clear space for what comes next.",
  waningCrescent: "The Moon is a thinning crescent, time to wind down: tie up loose ends and let go before the next cycle begins.",
};

// For the phase's Term note: the same day in Dane Rudhyar's eight 45-degree
// phases (core.lunationPhase), which astrologers often use instead.
export const rudhyarPhaseNote = (name) =>
  `Astrologers who follow Dane Rudhyar's eight phases call this stretch the ${name} phase.`;

// A planet retrograde right now (for everyone). 1 to 2 sentences, under 35 words, practical and calm.
export const RETRO_NOW = {
  mercury: "Mercury is retrograde, a good stretch to go back over plans and get back in touch with people. Double check travel plans, and give conversations a little extra patience.",
  venus: "Venus is retrograde, a time to revisit what you value and whose company you enjoy. Sit with your tastes and habits before making big changes to your look or your circle.",
  mars: "Mars is retrograde, so effort may feel slower or circle back to old tasks. Use it to finish what you started and to rethink how you handle anger.",
  jupiter: "Jupiter is retrograde, and growth turns inward for a few months. Good for reflecting on what you believe and whether your plans still fit who you are.",
  saturn: "Saturn is retrograde, a few months to review your commitments and routines. Shore up what matters, and set down the duties you have outgrown.",
  uranus: "Uranus is retrograde, so change works on the inside for a while. Notice where you want more freedom, and think it through before you act on it.",
  neptune: "Neptune is retrograde, which can give you a clearer view of what you have been idealizing or avoiding. Good for honest reflection and quiet creative work.",
  pluto: "Pluto is retrograde, a slow, private season for looking at what you are ready to let go of, even if you only let go of a little.",
};

// Fallback reflection prompts when nothing personal is strong today, keyed by Moon sign. One question each, under 16 words.
export const DAILY_PROMPTS = {
  aries: "What would you start today if you stopped waiting for permission?",
  taurus: "What simple pleasure would make today feel well spent?",
  gemini: "What question have you been curious about lately?",
  cancer: "Who or what makes you feel at home right now?",
  leo: "What would you make today purely for the fun of it?",
  virgo: "What one small fix would make your days easier?",
  libra: "Where in your life do you want more balance?",
  scorpio: "What are you still carrying that you'd happily put down?",
  sagittarius: "What would you like to learn more about this year?",
  capricorn: "What are you building, and is it still what you want?",
  aquarius: "What idea of yours goes against the grain, and why?",
  pisces: "What did you feel today that you have not put into words?",
};
